import { P } from "breadcommon";
import { TransactionColumn } from "./TransactionColumn";
import {
  ColumnSettings,
  defaultTransactionsNav,
  TransactionsNav,
} from "./TransactionsNavTypes";

// The `shown` field is excluded from serialization. It's derived from the
// current data set (e.g. the TYPE column auto-toggles based on whether any
// investment transactions exist) and isn't something the user controls
// directly — keeping it out keeps URLs shorter and prevents the auto-toggle
// from showing up as back-button history.
type SerializedColumnSettings = {
  predicate: P.SerializableOp | null;
};

type SerializedTransactionsNav = {
  sortColumn: TransactionColumn;
  sortOrder: number;
  searchString: string;
  searchStringExactMatch: boolean;
  columnSettings: Record<string, SerializedColumnSettings>;
};

export function serializeTransactionsNav(nav: TransactionsNav): string {
  const payload: Partial<SerializedTransactionsNav> = {};

  if (nav.sortColumn !== defaultTransactionsNav.sortColumn) {
    payload.sortColumn = nav.sortColumn;
  }
  if (nav.sortOrder !== defaultTransactionsNav.sortOrder) {
    payload.sortOrder = nav.sortOrder;
  }
  if (nav.searchString !== defaultTransactionsNav.searchString) {
    payload.searchString = nav.searchString;
  }
  if (
    nav.searchStringExactMatch !== defaultTransactionsNav.searchStringExactMatch
  ) {
    payload.searchStringExactMatch = nav.searchStringExactMatch;
  }

  const columnSettings: Record<string, SerializedColumnSettings> = {};
  for (const [key, settings] of Object.entries(nav.columnSettings)) {
    if (settings.predicate) {
      columnSettings[key] = {
        predicate: settings.predicate.asSerializable(),
      };
    }
  }
  if (Object.keys(columnSettings).length > 0) {
    payload.columnSettings = columnSettings;
  }

  return JSON.stringify(payload);
}

export function deserializeTransactionsNav(
  s: string,
): TransactionsNav | null {
  let parsed: Partial<SerializedTransactionsNav>;
  try {
    parsed = JSON.parse(s);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;

  const columnSettings: Record<TransactionColumn, ColumnSettings> = {
    ...defaultTransactionsNav.columnSettings,
  };
  const parsedColumnSettings = parsed.columnSettings ?? {};
  for (const [key, settings] of Object.entries(parsedColumnSettings)) {
    const col = parseInt(key, 10) as TransactionColumn;
    if (!(col in columnSettings)) continue;
    let predicate: P.Predicate | null = null;
    if (settings?.predicate) {
      try {
        predicate = P.deserialize(JSON.stringify(settings.predicate));
      } catch {
        predicate = null;
      }
    }
    columnSettings[col] = {
      ...columnSettings[col],
      predicate,
    };
  }

  return {
    sortColumn:
      typeof parsed.sortColumn === "number"
        ? (parsed.sortColumn as TransactionColumn)
        : defaultTransactionsNav.sortColumn,
    sortOrder:
      typeof parsed.sortOrder === "number"
        ? parsed.sortOrder
        : defaultTransactionsNav.sortOrder,
    searchString:
      typeof parsed.searchString === "string"
        ? parsed.searchString
        : defaultTransactionsNav.searchString,
    searchStringExactMatch:
      typeof parsed.searchStringExactMatch === "boolean"
        ? parsed.searchStringExactMatch
        : defaultTransactionsNav.searchStringExactMatch,
    columnSettings,
  };
}
